import { UzDialog, UzSpinner } from '@utazzo/ui';
import React from 'react';

import { useAgentTargetsList } from '../use-agent-targets';

interface AgentTargetHistoryDialogProps {
  userId: string;
  userName: string;
  open: boolean;
  onClose: () => void;
}

const PERIOD_LABELS: Record<string, string> = {
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUAL: 'Annual',
};

const BASIS_LABELS: Record<string, string> = {
  TURNOVER: 'Turnover',
  REVENUE: 'Revenue',
};

/**
 * Read-only popup listing every target ever set for one agent, newest first -
 * the active row plus all superseded (historical) rows. Admin-only; opened
 * from the "Previous targets" info button on the Agent Targets list.
 */
export const AgentTargetHistoryDialog = ({
  userId,
  userName,
  open,
  onClose,
}: AgentTargetHistoryDialogProps): React.JSX.Element => {
  const query = useAgentTargetsList({
    page: 1,
    // Max page size - a single agent's monthly history won't realistically
    // exceed this, so every target loads in one request (the popup scrolls).
    pageSize: 200,
    sortBy: 'periodStart',
    sortDir: 'desc',
    userId,
    includeHistory: true,
  });
  const rows = query.data?.data ?? [];

  return (
    <UzDialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Target history - ${userName}`}
      description="Every target set for this agent, newest first. The current one is marked Active; the rest are superseded (historical) rows."
      size="lg"
    >
      {query.isLoading ? (
        <div className="flex items-center justify-center py-10">
          <UzSpinner />
        </div>
      ) : query.isError ? (
        <p className="py-8 text-center text-sm text-red-600">
          Failed to load target history. Try again.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-500">
          No targets have been set for this agent yet.
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {rows.map((r) => {
            const active = r.periodEnd === null;
            return (
              <li key={r.id} className="flex items-start justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {active ? (
                      <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-500">
                        Superseded
                      </span>
                    )}
                    <span className="text-sm text-zinc-700">
                      {active ? `since ${r.periodStart}` : `${r.periodStart} - ${r.periodEnd}`}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {PERIOD_LABELS[r.period] ?? r.period}
                    {r.targetBasis ? ` · ${BASIS_LABELS[r.targetBasis] ?? r.targetBasis}` : ''} ·
                    set {r.createdAt.slice(0, 10)}
                  </p>
                </div>
                <span className="shrink-0 text-sm font-semibold text-zinc-900 tabular-nums">
                  {r.targetAmount.toLocaleString('en-IN')}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </UzDialog>
  );
};
